import React, { useEffect, useState } from "react";
import { RequestField } from "./RequestField";
import { FieldState } from "~/components/inline-fields-form/types";
import { ProjectRequest } from "~/gql/types";
import { FolderTypePicker } from "~/project-requests/ProjectRequestForm/FolderTypePicker";
import {
  EditFolderDrive,
  EditGroupedFolder,
  EditProjectTextField,
} from "~/ProjectForm/EditProjectComponents";
import { ProjectRequestFolderType } from "./types";
import { useGetGroupedFoldersQuery } from "~/refdata/groupedFolderSearch.hotchoc.generated";
import styled from "@emotion/styled";
import { SharedDrive } from "~/gql/types2";
import { useSharedDrivesAsync } from "~/refdata2/sharedDrives";

type FolderFieldProps = {
  formState: Record<string, FieldState>;
  diffIconMenuProps: { otherRequests: ProjectRequest[] | undefined };
};

export const FolderRequestFields: React.FC<FolderFieldProps> = (props) => {
  const { formState, diffIconMenuProps } = props;
  const [drives, setDrives] = useState<SharedDrive[] | null>(null);
  const loading = drives === null;

  const getSharedDrives = useSharedDrivesAsync();

  useEffect(() => {
    const handle = async () => {
      const res = await getSharedDrives();

      setDrives(res ?? []);
    };

    void handle();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <>
        <dt></dt>
        <dd>Loading...</dd>
      </>
    );
  }

  return (
    <>
      <RequestField
        {...{
          label: "Folder type",
          name: "folderType",
          formState,
          ...diffIconMenuProps,
        }}
      >
        <FolderTypePicker name="folderType" />
      </RequestField>

      {formState.folderType?.value ==
        ProjectRequestFolderType.ExistingGrouped && (
        <>
          {!formState.customer?.value?.customerId ||
          formState.requestNewCustomer.value === true ? (
            <PickerStatusLabel
              text="Please select an existing customer first"
              error={!!formState?.groupedFolder.error}
            />
          ) : (
            <GroupedFolderField
              customerId={formState.customer.value.customerId}
              formState={formState}
              diffIconMenuProps={diffIconMenuProps}
            />
          )}
        </>
      )}
      {formState.folderType?.value == ProjectRequestFolderType.Single && (
        <RequestField
          {...{
            label: "Jobs folder drive letter",
            name: "jobsFolderDriveLetter",
            formState,
            ...diffIconMenuProps,
          }}
        >
          <EditFolderDrive name="jobsFolderDriveLetter" folderDrives={drives} />
        </RequestField>
      )}
      {formState.folderType?.value == ProjectRequestFolderType.NewGrouped && (
        <>
          <RequestField
            {...{
              label: "New folder name",
              name: "newGroupedFolderName",
              formState,
              ...diffIconMenuProps,
            }}
          >
            <EditProjectTextField name="newGroupedFolderName" />
          </RequestField>
          <RequestField
            {...{
              label: "New folder drive letter",
              name: "newGroupedFolderDriveLetter",
              formState,
              ...diffIconMenuProps,
            }}
          >
            <EditFolderDrive
              name="newGroupedFolderDriveLetter"
              folderDrives={drives}
            />
          </RequestField>
        </>
      )}
    </>
  );
};

type GroupedFolderFieldProps = {
  customerId: number;
  formState: Record<string, FieldState>;
  diffIconMenuProps: { otherRequests: ProjectRequest[] | undefined };
};

const GroupedFolderField: React.FC<GroupedFolderFieldProps> = (props) => {
  const { customerId, formState, diffIconMenuProps } = props;

  const { loading, data } = useGetGroupedFoldersQuery({
    variables: {
      input: {
        customerId: customerId,
      },
    },
  });

  if (loading) {
    return <PickerStatusLabel text={"Loading..."} error={false} />;
  }

  if (!data || data.projectFolders.groupedFolders.length === 0) {
    return (
      <PickerStatusLabel
        text={"No grouped folders found for the selected customer"}
        error={!!formState?.groupedFolder.error}
      />
    );
  }

  return (
    <RequestField
      {...{
        label: "Existing grouped folder",
        name: "groupedFolder",
        formState,
        ...diffIconMenuProps,
      }}
    >
      <EditGroupedFolder
        name="groupedFolder"
        groupedFolders={data.projectFolders.groupedFolders}
      />
    </RequestField>
  );
};

type PickerStatusLabelProps = {
  text: string;
  error: boolean;
};

const PickerStatusLabel: React.FC<PickerStatusLabelProps> = ({
  text,
  error,
}) => {
  const Label = styled("dt")`
    display: flex !important;
    padding-top: 5px;
    min-height: 25px;
  `;

  const Message = styled("dd")`
    margin-bottom: 0 !important;
    ${error &&
    "border: 2px solid var(--mdc-theme-secondary); background-color: white"};
  `;

  return (
    <>
      <Label>Existing grouped folder</Label>
      <Message>{text}</Message>
    </>
  );
};
